import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Tooth } from './Tooth';
import type { ToothData, ToothNotation } from './types';
import { getPrimaryStatus } from './types';
import { formatTooth } from '@/utils/toothNotation';

// ── Dentition mode ─────────────────────────────────────────────────────────────

export type DentitionMode = 'adult' | 'child' | 'mixed';

// ── Arch layout ───────────────────────────────────────────────────────────────

const ADULT_UPPER_RIGHT = ['18', '17', '16', '15', '14', '13', '12', '11'];
const ADULT_UPPER_LEFT  = ['21', '22', '23', '24', '25', '26', '27', '28'];
const ADULT_LOWER_LEFT  = ['31', '32', '33', '34', '35', '36', '37', '38'];
const ADULT_LOWER_RIGHT = ['48', '47', '46', '45', '44', '43', '42', '41'];

const CHILD_UPPER_RIGHT = ['55', '54', '53', '52', '51'];
const CHILD_UPPER_LEFT  = ['61', '62', '63', '64', '65'];
const CHILD_LOWER_LEFT  = ['71', '72', '73', '74', '75'];
const CHILD_LOWER_RIGHT = ['85', '84', '83', '82', '81'];

// ── Props ─────────────────────────────────────────────────────────────────────

interface ToothChartCanvasProps {
  teeth: ToothData[];
  selectedTeeth: string[];
  onToothClick: (fdi: string, event: React.MouseEvent) => void;
  notation: ToothNotation;
  dentition: DentitionMode;
  readOnly?: boolean;
  badgeMap?: Map<string, { hasImage: boolean; hasPlan: boolean; hasNote: boolean }>;
}

// ── Quadrant pill labels ───────────────────────────────────────────────────────

function QuadrantLabel({ children, align }: { children: React.ReactNode; align: 'left' | 'right' }) {
  return (
    <div
      className={cn(
        'absolute top-1.5 z-10',
        align === 'left' ? 'left-2' : 'right-2',
      )}
    >
      <span className="inline-block rounded-md border border-stone-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-stone-500 shadow-sm">
        {children}
      </span>
    </div>
  );
}

function QuadrantLabelBottom({ children, align }: { children: React.ReactNode; align: 'left' | 'right' }) {
  return (
    <div
      className={cn(
        'absolute bottom-1.5 z-10',
        align === 'left' ? 'left-2' : 'right-2',
      )}
    >
      <span className="inline-block rounded-md border border-stone-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-stone-500 shadow-sm">
        {children}
      </span>
    </div>
  );
}

// ── Tooth wrapper with tooltip ────────────────────────────────────────────────

interface ToothWrapperProps {
  fdi: string;
  tooth?: ToothData;
  selected: boolean;
  notation: ToothNotation;
  onClick: (e: React.MouseEvent) => void;
  readOnly: boolean;
  hasImage?: boolean;
  hasPlan?: boolean;
  hasNote?: boolean;
  scale?: number;
}

const ToothWrapper = React.memo(function ToothWrapper({
  fdi,
  tooth,
  selected,
  notation,
  onClick,
  readOnly,
  hasImage,
  hasPlan,
  hasNote,
  scale = 1,
}: ToothWrapperProps) {
  const displayNumber = formatTooth(fdi, notation);

  const primaryStatus = getPrimaryStatus(tooth?.status ?? ['healthy']);
  const statusLabels: Record<string, string> = {
    healthy: 'Rask', caries: 'Caries', filled: 'Fyldning', crown: 'Krone',
    missing: 'Mangler', implant: 'Implantat', root_canal: 'Rodbehandlet',
    bridge: 'Bro', extraction_planned: 'Planl. udtræk', fracture: 'Fraktur',
    periapical_abscess: 'Periap. absces', veneer: 'Finér', post_and_core: 'Stift/opbygning',
  };
  const lastSeen = tooth?.last_examined_at
    ? new Date(tooth.last_examined_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const tooltipParts = [
    `Tand ${displayNumber}`,
    statusLabels[primaryStatus] ?? primaryStatus,
    lastSeen ? `Sidst set ${lastSeen}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div
      key={fdi}
      className="relative"
      title={tooltipParts}
      style={scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: 'bottom center' } : undefined}
    >
      <Tooth
        fdi={fdi}
        tooth={tooth}
        selected={selected}
        notation={notation}
        onClick={onClick}
        readOnly={readOnly}
        hasImage={hasImage}
        hasPlan={hasPlan}
        hasNote={hasNote}
      />
    </div>
  );
});

// ── Main canvas ───────────────────────────────────────────────────────────────

export function ToothChartCanvas({
  teeth,
  selectedTeeth,
  onToothClick,
  notation,
  dentition,
  readOnly = false,
  badgeMap,
}: ToothChartCanvasProps) {
  const { t } = useTranslation();

  const teethMap = useMemo(() => {
    const m = new Map<string, ToothData>();
    teeth.forEach((tooth) => m.set(tooth.tooth_fdi, tooth));
    return m;
  }, [teeth]);

  const handleClick = (fdi: string, e: React.MouseEvent) => {
    if (!readOnly) onToothClick(fdi, e);
  };

  const renderTooth = (fdi: string, scale?: number) => (
    <ToothWrapper
      key={fdi}
      fdi={fdi}
      tooth={teethMap.get(fdi)}
      selected={selectedTeeth.includes(fdi)}
      notation={notation}
      onClick={(e) => handleClick(fdi, e)}
      readOnly={readOnly}
      hasImage={badgeMap?.get(fdi)?.hasImage}
      hasPlan={badgeMap?.get(fdi)?.hasPlan}
      hasNote={badgeMap?.get(fdi)?.hasNote}
      scale={scale}
    />
  );

  const q1Label = t('dental.chart.quadrants.q1', 'Q1 — Overkæbe højre');
  const q2Label = t('dental.chart.quadrants.q2', 'Q2 — Overkæbe venstre');
  const q3Label = t('dental.chart.quadrants.q3', 'Q3 — Underkæbe venstre');
  const q4Label = t('dental.chart.quadrants.q4', 'Q4 — Underkæbe højre');

  // ── Adult layout ──────────────────────────────────────────────────────────

  if (dentition === 'adult') {
    return (
      <div className="w-full rounded-2xl border border-stone-200 bg-white shadow-sm dark:border-stone-700 dark:bg-stone-900 overflow-hidden">
        <div className="relative bg-stone-50/70 dark:bg-stone-900/40 pt-8 pb-2 px-3">
          <QuadrantLabel align="left">{q1Label}</QuadrantLabel>
          <QuadrantLabel align="right">{q2Label}</QuadrantLabel>
          <div className="flex items-end justify-center gap-1">
            <div className="flex items-end gap-1 pr-2 border-r-2 border-stone-300 dark:border-stone-600">
              {ADULT_UPPER_RIGHT.map((fdi) => renderTooth(fdi))}
            </div>
            <div className="flex items-end gap-1 pl-2">
              {ADULT_UPPER_LEFT.map((fdi) => renderTooth(fdi))}
            </div>
          </div>
        </div>

        <Midline />

        <div className="relative bg-stone-50/70 dark:bg-stone-900/40 pt-2 pb-8 px-3">
          <QuadrantLabelBottom align="left">{q4Label}</QuadrantLabelBottom>
          <QuadrantLabelBottom align="right">{q3Label}</QuadrantLabelBottom>
          <div className="flex items-start justify-center gap-1">
            <div className="flex items-start gap-1 pr-2 border-r-2 border-stone-300 dark:border-stone-600">
              {ADULT_LOWER_RIGHT.map((fdi) => renderTooth(fdi))}
            </div>
            <div className="flex items-start gap-1 pl-2">
              {ADULT_LOWER_LEFT.map((fdi) => renderTooth(fdi))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Child layout ──────────────────────────────────────────────────────────

  if (dentition === 'child') {
    return (
      <div className="w-full rounded-2xl border border-stone-200 bg-white shadow-sm dark:border-stone-700 dark:bg-stone-900 overflow-hidden">
        <div className="relative bg-stone-50/70 dark:bg-stone-900/40 pt-8 pb-2 px-3">
          <QuadrantLabel align="left">Q5 — Overkæbe højre (primær)</QuadrantLabel>
          <QuadrantLabel align="right">Q6 — Overkæbe venstre (primær)</QuadrantLabel>
          <div className="flex items-end justify-center gap-1">
            <div className="flex items-end gap-1 pr-2 border-r-2 border-stone-300 dark:border-stone-600">
              {CHILD_UPPER_RIGHT.map((fdi) => renderTooth(fdi))}
            </div>
            <div className="flex items-end gap-1 pl-2">
              {CHILD_UPPER_LEFT.map((fdi) => renderTooth(fdi))}
            </div>
          </div>
        </div>

        <Midline />

        <div className="relative bg-stone-50/70 dark:bg-stone-900/40 pt-2 pb-8 px-3">
          <QuadrantLabelBottom align="left">Q8 — Underkæbe højre (primær)</QuadrantLabelBottom>
          <QuadrantLabelBottom align="right">Q7 — Underkæbe venstre (primær)</QuadrantLabelBottom>
          <div className="flex items-start justify-center gap-1">
            <div className="flex items-start gap-1 pr-2 border-r-2 border-stone-300 dark:border-stone-600">
              {CHILD_LOWER_RIGHT.map((fdi) => renderTooth(fdi))}
            </div>
            <div className="flex items-start gap-1 pl-2">
              {CHILD_LOWER_LEFT.map((fdi) => renderTooth(fdi))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Mixed dentition ───────────────────────────────────────────────────────

  return (
    <div className="w-full rounded-2xl border border-stone-200 bg-white shadow-sm dark:border-stone-700 dark:bg-stone-900 overflow-hidden">
      <div className="relative bg-stone-50/70 dark:bg-stone-900/40 pt-8 pb-1 px-3">
        <QuadrantLabel align="left">{q1Label}</QuadrantLabel>
        <QuadrantLabel align="right">{q2Label}</QuadrantLabel>
        <div className="flex items-end justify-center gap-1">
          <div className="flex items-end gap-1 pr-2 border-r-2 border-stone-300 dark:border-stone-600">
            {ADULT_UPPER_RIGHT.map((fdi) => renderTooth(fdi))}
          </div>
          <div className="flex items-end gap-1 pl-2">
            {ADULT_UPPER_LEFT.map((fdi) => renderTooth(fdi))}
          </div>
        </div>
      </div>

      {/* Upper primary row */}
      <div className="bg-amber-50/30 dark:bg-amber-900/10 border-y border-amber-200/50 dark:border-amber-900/30 px-3 py-1">
        <div className="flex items-center justify-center gap-1">
          <span className="mr-2 text-[9px] font-semibold uppercase tracking-wider text-amber-600/70">Primær</span>
          <div className="flex items-center gap-1 pr-2 border-r border-amber-200/60 dark:border-amber-800">
            {CHILD_UPPER_RIGHT.map((fdi) => renderTooth(fdi, 0.75))}
          </div>
          <div className="flex items-center gap-1 pl-2">
            {CHILD_UPPER_LEFT.map((fdi) => renderTooth(fdi, 0.75))}
          </div>
        </div>
      </div>

      <Midline />

      {/* Lower primary row */}
      <div className="bg-amber-50/30 dark:bg-amber-900/10 border-y border-amber-200/50 dark:border-amber-900/30 px-3 py-1">
        <div className="flex items-center justify-center gap-1">
          <span className="mr-2 text-[9px] font-semibold uppercase tracking-wider text-amber-600/70">Primær</span>
          <div className="flex items-center gap-1 pr-2 border-r border-amber-200/60 dark:border-amber-800">
            {CHILD_LOWER_RIGHT.map((fdi) => renderTooth(fdi, 0.75))}
          </div>
          <div className="flex items-center gap-1 pl-2">
            {CHILD_LOWER_LEFT.map((fdi) => renderTooth(fdi, 0.75))}
          </div>
        </div>
      </div>

      <div className="relative bg-stone-50/70 dark:bg-stone-900/40 pt-1 pb-8 px-3">
        <QuadrantLabelBottom align="left">{q4Label}</QuadrantLabelBottom>
        <QuadrantLabelBottom align="right">{q3Label}</QuadrantLabelBottom>
        <div className="flex items-start justify-center gap-1">
          <div className="flex items-start gap-1 pr-2 border-r-2 border-stone-300 dark:border-stone-600">
            {ADULT_LOWER_RIGHT.map((fdi) => renderTooth(fdi))}
          </div>
          <div className="flex items-start gap-1 pl-2">
            {ADULT_LOWER_LEFT.map((fdi) => renderTooth(fdi))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function Midline() {
  return (
    <div className="relative flex items-center gap-3 px-4 py-2 bg-white dark:bg-stone-900 border-y border-stone-200 dark:border-stone-700">
      <div className="flex-1 border-t border-dashed border-stone-300 dark:border-stone-600" />
      <span className="rounded-md border border-stone-200 bg-white px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-stone-400 whitespace-nowrap shadow-sm">
        Overkæbe · Underkæbe
      </span>
      <div className="flex-1 border-t border-dashed border-stone-300 dark:border-stone-600" />
    </div>
  );
}
